import { useCallback, useEffect, useState } from 'react';
import api from '../services/api';
import type { EmailSpyConnection } from '../types';

// The email spy lives in the mailer. Recon reaches it with an API key made in the mailer's
// Settings › API Keys, to get spy addresses for competitors' signup forms. Each key belongs to one
// mailer organization, so there is one connection per org. Each recon room uses the default
// connection unless another is picked on its Email capture report. Each address comes with its own
// made-up person (name, company, job title, country) from the mailer, so there is no profile here.

const INPUT = 'w-full border border-gray-300 dark:border-gray-600 dark:bg-gray-900 rounded px-3 py-2 text-sm';
const LINK = 'text-indigo-600 dark:text-indigo-400 hover:underline disabled:opacity-50';

type Form = { name: string; mailer_url: string; api_key: string };
const EMPTY: Form = { name: '', mailer_url: '', api_key: '' };

export default function EmailSpySettingsPage() {
  const [connections, setConnections] = useState<EmailSpyConnection[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  // null = form closed; 'new' = adding; a number = editing that connection.
  const [editing, setEditing] = useState<number | 'new' | null>(null);
  const [form, setForm] = useState<Form>(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      setConnections(await api.listEmailSpyConnections());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load connections');
    }
  }, []);

  useEffect(() => {
    load().finally(() => setLoading(false));
  }, [load]);

  const clear = () => {
    setError('');
    setMessage('');
  };

  const openAdd = () => {
    clear();
    setForm(EMPTY);
    setEditing('new');
  };

  const openEdit = (c: EmailSpyConnection) => {
    clear();
    setForm({ name: c.name, mailer_url: c.mailer_url ?? '', api_key: '' });
    setEditing(c.id);
  };

  const set = (key: keyof Form, value: string) => setForm(f => ({ ...f, [key]: value }));

  const save = async () => {
    clear();
    setSaving(true);
    try {
      const name = form.name.trim();
      const mailer_url = form.mailer_url.trim();
      const api_key = form.api_key.trim();
      if (editing === 'new') {
        await api.createEmailSpyConnection({ name, mailer_url, api_key });
      } else if (editing != null) {
        // An empty key box means "keep the saved key".
        await api.updateEmailSpyConnection(editing, api_key ? { name, mailer_url, api_key } : { name, mailer_url });
      }
      setEditing(null);
      setMessage('Saved.');
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const test = async (c: EmailSpyConnection) => {
    clear();
    try {
      const got = await api.testEmailSpyConnection(c.id);
      setMessage(`${c.name}: connected to ${got.organization_name || 'the mailer'}.`);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not reach the mailer');
    }
  };

  const makeDefault = async (c: EmailSpyConnection) => {
    clear();
    try {
      await api.updateEmailSpyConnection(c.id, { is_default: true });
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not make it the default');
    }
  };

  const remove = async (c: EmailSpyConnection) => {
    clear();
    if (!window.confirm(`Delete the connection "${c.name}"?`)) return;
    try {
      await api.deleteEmailSpyConnection(c.id);
      if (editing === c.id) setEditing(null);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not delete it');
    }
  };

  if (loading) return <div className="text-center py-8 text-gray-500 dark:text-gray-400">Loading...</div>;

  const editingOne = typeof editing === 'number' ? connections.find(c => c.id === editing) : undefined;

  const formBox = editing != null && (
    <div className="bg-white dark:bg-gray-800 border rounded-lg px-4 py-4 space-y-3 mt-4">
      <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
        {editing === 'new' ? 'Add connection' : `Edit ${editingOne?.name ?? 'connection'}`}
      </p>
      <label className="block text-sm text-gray-700 dark:text-gray-300">
        Name
        <input className={INPUT} value={form.name} onChange={e => set('name', e.target.value)} placeholder="Acme mailer" />
      </label>
      <label className="block text-sm text-gray-700 dark:text-gray-300">
        Mailer address
        <input
          className={INPUT}
          value={form.mailer_url}
          onChange={e => set('mailer_url', e.target.value)}
          placeholder="https://app.contactanchor.com"
        />
      </label>
      <label className="block text-sm text-gray-700 dark:text-gray-300">
        API key
        <input
          className={INPUT}
          type="password"
          value={form.api_key}
          onChange={e => set('api_key', e.target.value)}
          placeholder={editingOne?.has_api_key ? 'Saved. Paste a new key to replace it.' : 'cak_...'}
        />
      </label>
      <div className="flex gap-2">
        <button
          type="button"
          onClick={save}
          disabled={saving || !form.name.trim() || (editing === 'new' && (!form.mailer_url.trim() || !form.api_key.trim()))}
          className="px-4 py-2 bg-indigo-600 text-white rounded text-sm disabled:opacity-50"
        >
          {saving ? 'Saving...' : 'Save'}
        </button>
        <button
          type="button"
          onClick={() => setEditing(null)}
          className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded text-sm text-gray-700 dark:text-gray-200"
        >
          Cancel
        </button>
      </div>
    </div>
  );

  return (
    <div className="max-w-2xl">
      <h2 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-1">Email spy</h2>
      <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">
        Recon signs up to competitors' email forms with addresses from your email spy, so their emails land there. Add
        one connection per mailer organization: make an API key in that org's mailer under Settings › API Keys and
        paste it here. Each recon room uses the default connection unless you pick another on its Email capture report.
      </p>
      <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
        Each address gets its own made-up name, company and job title for forms that ask for more than an email. No
        phone number is ever made up, so forms that need one are left for you.
      </p>

      {error && (
        <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}
      {message && <div className="text-sm text-green-700 dark:text-green-400 mb-4">{message}</div>}

      {connections.length === 0 ? (
        <p className="text-sm text-gray-500 dark:text-gray-400">No connections yet.</p>
      ) : (
        <ul className="bg-white dark:bg-gray-800 border rounded-lg divide-y divide-gray-100 dark:divide-gray-700">
          {connections.map(c => (
            <li key={c.id} className="px-4 py-3 text-sm" data-testid={`connection-${c.id}`}>
              <div className="flex flex-wrap items-baseline gap-2">
                <span className="font-medium text-gray-900 dark:text-gray-100">{c.name}</span>
                {c.is_default && (
                  <span className="px-1.5 py-0.5 rounded bg-indigo-100 dark:bg-indigo-900/40 text-indigo-700 dark:text-indigo-300 text-xs">
                    Default
                  </span>
                )}
              </div>
              <div className="text-gray-500 dark:text-gray-400">
                Mailer org: {c.organization_name || 'not tested yet'} · {c.signups} sign-up{c.signups === 1 ? '' : 's'}
                {!c.has_api_key && ' · no API key'}
              </div>
              <div className="flex flex-wrap gap-3 mt-1">
                <button type="button" className={LINK} onClick={() => openEdit(c)}>
                  Edit
                </button>
                <button type="button" className={LINK} onClick={() => test(c)} disabled={!c.has_api_key || !c.mailer_url}>
                  Test connection
                </button>
                {!c.is_default && (
                  <button type="button" className={LINK} onClick={() => makeDefault(c)}>
                    Make default
                  </button>
                )}
                <button type="button" className={LINK} onClick={() => remove(c)}>
                  Delete
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}

      {formBox}

      {editing == null && (
        <button
          type="button"
          onClick={openAdd}
          className="mt-4 px-4 py-2 bg-indigo-600 text-white rounded text-sm"
        >
          Add connection
        </button>
      )}
    </div>
  );
}
